import { hotelSubsidiaryPmsHealthyKinds } from 'main/hotelStatuses'

export const NeedsAttentionProblemKeys = {
  NeverSynced: 'NeverSynced',
  PartiallySynced: 'PartiallySynced',
  SyncStopped: 'SyncStopped',
  TcrOff: 'TcrOff',
  InviteBlocked: 'InviteBlocked',
  NoServiceStart: 'NoServiceStart',
  PhoneProblem: 'PhoneProblem',
  PlatformFeeOff: 'PlatformFeeOff',
  CampaignsOff: 'CampaignsOff'
}

/** Chip order above the Needs Attention list */
export const NEEDS_ATTENTION_CHIP_ORDER = [
  NeedsAttentionProblemKeys.NeverSynced,
  NeedsAttentionProblemKeys.PartiallySynced,
  NeedsAttentionProblemKeys.SyncStopped,
  NeedsAttentionProblemKeys.TcrOff,
  NeedsAttentionProblemKeys.InviteBlocked,
  NeedsAttentionProblemKeys.NoServiceStart,
  NeedsAttentionProblemKeys.PhoneProblem,
  NeedsAttentionProblemKeys.PlatformFeeOff,
  NeedsAttentionProblemKeys.CampaignsOff
]

export const NEEDS_ATTENTION_CHIP_LABELS = {
  [NeedsAttentionProblemKeys.NeverSynced]: 'Never synced',
  [NeedsAttentionProblemKeys.PartiallySynced]: 'Partially synced',
  [NeedsAttentionProblemKeys.SyncStopped]: 'PMS sync stopped',
  [NeedsAttentionProblemKeys.TcrOff]: 'TCR off',
  [NeedsAttentionProblemKeys.InviteBlocked]: 'Invite blocked',
  [NeedsAttentionProblemKeys.NoServiceStart]: 'No service start',
  [NeedsAttentionProblemKeys.PhoneProblem]: 'Phone problem',
  [NeedsAttentionProblemKeys.PlatformFeeOff]: 'Platform fee off',
  [NeedsAttentionProblemKeys.CampaignsOff]: 'Campaigns off'
}

const SYNC_STOPPED_AFTER_NO_DATA_DAYS = 7

/** Matches Domain.Merchants.Enums.SubsidiaryPmsHealthyKind numeric values. */
const PMS_HEALTHY_KIND_BY_NUMBER = {
  0: 'Unknown',
  1: 'PasswordWrong',
  2: 'PasswordExpired',
  3: 'NotIntegrated',
  4: 'SyncStopped',
  5: 'PartiallyWorking'
}

/**
 * @param {unknown} value
 * @returns {boolean | null} null when unknown / missing
 */
export function readOptionalBool(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return true
  }
  if (value === false || value === 'false' || value === 0 || value === '0') {
    return false
  }
  return null
}

/**
 * @param {object} hotel
 * @returns {boolean} true when PMS is healthy/connected
 */
export function readPmsSyncIsConnected(hotel) {
  const flag = readOptionalBool(hotel?.pmsSyncIsConnected ?? hotel?.PmsSyncIsConnected)
  return flag === true
}

/**
 * @param {unknown} kind
 * @returns {string}
 */
export function normalizePmsHealthyKind(kind) {
  if (kind == null || kind === '') {
    return ''
  }
  if (typeof kind === 'number' || /^\d+$/.test(String(kind))) {
    return PMS_HEALTHY_KIND_BY_NUMBER[Number(kind)] || String(kind)
  }
  return String(kind)
}

/**
 * @param {string | undefined | null} sinceIso
 * @returns {number | null}
 */
export function needsAttentionAgeInDays(sinceIso) {
  if (sinceIso == null || sinceIso === '') {
    return null
  }
  const start = new Date(sinceIso)
  if (Number.isNaN(start.getTime())) {
    return null
  }
  const startDay = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
  const now = new Date()
  const nowDay = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return Math.max(0, Math.round((nowDay - startDay) / 86400000))
}

/**
 * Inclusive problem keys for a hotel. Prefers API NeedsAttentionProblems when present.
 * When Partial is stored but since age is ≥ 7 days, promotes to SyncStopped (matches write-path rule).
 * @param {object} hotel
 * @returns {string[]}
 */
export function classifyNeedsAttentionProblems(hotel) {
  if (!hotel) {
    return []
  }

  const fromApi = hotel.needsAttentionProblems ?? hotel.NeedsAttentionProblems
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    return promoteStalePartial(fromApi, hotel)
  }

  const problems = []
  const pmsConnected = readPmsSyncIsConnected(hotel)
  const kind = normalizePmsHealthyKind(hotel.pmsHealthyKind ?? hotel.PmsHealthyKind)
  const since =
    hotel.pmsLastDataOn ?? hotel.PmsLastDataOn ?? hotel.needsAttentionSince ?? hotel.NeedsAttentionSince
  const ageDays = needsAttentionAgeInDays(since)

  if (!pmsConnected) {
    if (kind === 'NotIntegrated') {
      problems.push(NeedsAttentionProblemKeys.NeverSynced)
    } else if (kind === 'PartiallyWorking') {
      if (ageDays != null && ageDays >= SYNC_STOPPED_AFTER_NO_DATA_DAYS) {
        problems.push(NeedsAttentionProblemKeys.SyncStopped)
      } else {
        problems.push(NeedsAttentionProblemKeys.PartiallySynced)
      }
    } else if (
      kind === 'SyncStopped' ||
      kind === 'PasswordWrong' ||
      kind === 'PasswordExpired' ||
      !kind
    ) {
      problems.push(NeedsAttentionProblemKeys.SyncStopped)
    } else if (hotelSubsidiaryPmsHealthyKinds[kind]) {
      problems.push(NeedsAttentionProblemKeys.SyncStopped)
    } else {
      // Unknown kind but PMS is disconnected — still a sync-stopped problem for chips/filters.
      problems.push(NeedsAttentionProblemKeys.SyncStopped)
    }
  }

  const tcrOn = readOptionalBool(hotel.isTcrActivated ?? hotel.IsTcrActivated)
  if (tcrOn === false) {
    problems.push(NeedsAttentionProblemKeys.TcrOff)
  }

  const phoneOn = readOptionalBool(hotel.isPhoneEnabled ?? hotel.IsPhoneEnabled)
  if (phoneOn === false) {
    problems.push(NeedsAttentionProblemKeys.PhoneProblem)
  }

  const platformFeeOn = readOptionalBool(
    hotel.isEnabledPlatformAccessFee ?? hotel.IsEnabledPlatformAccessFee
  )
  if (platformFeeOn === false) {
    problems.push(NeedsAttentionProblemKeys.PlatformFeeOff)
  }

  if (hotel.inviteBlockOn ?? hotel.InviteBlockOn) {
    problems.push(NeedsAttentionProblemKeys.InviteBlocked)
  }

  const campaignsOn = readOptionalBool(hotel.isCampaignsEnabled ?? hotel.IsCampaignsEnabled)
  if (campaignsOn === false) {
    problems.push(NeedsAttentionProblemKeys.CampaignsOff)
  }

  const started = readOptionalBool(hotel.isServiceStarted ?? hotel.IsServiceStarted)
  const status = hotel.status ?? hotel.Status
  if (started === false && status === 'BasicAccount') {
    problems.push(NeedsAttentionProblemKeys.NoServiceStart)
  }

  return problems
}

/**
 * @param {string[]} problems
 * @param {object} hotel
 * @returns {string[]}
 */
function promoteStalePartial(problems, hotel) {
  const normalized = (problems || []).map(p => String(p))
  if (!normalized.includes(NeedsAttentionProblemKeys.PartiallySynced)) {
    return normalized
  }
  const since =
    hotel.pmsLastDataOn ?? hotel.PmsLastDataOn ?? hotel.needsAttentionSince ?? hotel.NeedsAttentionSince
  const ageDays = needsAttentionAgeInDays(since)
  if (ageDays == null || ageDays < SYNC_STOPPED_AFTER_NO_DATA_DAYS) {
    return normalized
  }
  return normalized
    .filter(p => p !== NeedsAttentionProblemKeys.PartiallySynced)
    .concat(
      normalized.includes(NeedsAttentionProblemKeys.SyncStopped)
        ? []
        : [NeedsAttentionProblemKeys.SyncStopped]
    )
}

/**
 * @param {object[]} hotels
 * @returns {Record<string, number>}
 */
export function countNeedsAttentionProblems(hotels) {
  const counts = {}
  for (const key of NEEDS_ATTENTION_CHIP_ORDER) {
    counts[key] = 0
  }
  for (const hotel of hotels || []) {
    const problems = classifyNeedsAttentionProblems(hotel)
    for (const key of problems) {
      if (counts[key] == null) {
        counts[key] = 0
      }
      counts[key] += 1
    }
  }
  return counts
}

/**
 * @param {object} hotel
 * @param {string} problemKey
 * @returns {boolean}
 */
export function hotelHasNeedsAttentionProblem(hotel, problemKey) {
  if (!problemKey || problemKey === 'all') {
    return true
  }
  return classifyNeedsAttentionProblems(hotel).includes(problemKey)
}

/** Problem groups that are PMS sync related — show PMS breakdown chips when selected. */
export const PMS_NEEDS_ATTENTION_PROBLEM_KEYS = [
  NeedsAttentionProblemKeys.NeverSynced,
  NeedsAttentionProblemKeys.PartiallySynced,
  NeedsAttentionProblemKeys.SyncStopped
]

/**
 * @param {string} problemKey
 * @returns {boolean}
 */
export function isPmsNeedsAttentionProblem(problemKey) {
  return PMS_NEEDS_ATTENTION_PROBLEM_KEYS.includes(problemKey)
}

/**
 * @param {object} hotel
 * @returns {string}
 */
export function readHotelPmsKey(hotel) {
  const raw = (hotel?.pms ?? hotel?.Pms ?? '').toString().trim()
  return (raw || 'unknown').toLowerCase()
}

/**
 * Count hotels by PMS within a selected PMS problem group, highest count first.
 * @param {object[]} hotels
 * @param {string} problemKey
 * @param {(pmsKey: string) => string} [labelForPms]
 * @returns {{ pms: string, label: string, count: number }[]}
 */
export function countPmsForNeedsAttentionProblem(hotels, problemKey, labelForPms) {
  if (!isPmsNeedsAttentionProblem(problemKey)) {
    return []
  }

  const counts = {}
  for (const hotel of hotels || []) {
    if (!hotelHasNeedsAttentionProblem(hotel, problemKey)) {
      continue
    }
    const pms = readHotelPmsKey(hotel)
    counts[pms] = (counts[pms] || 0) + 1
  }

  return Object.keys(counts)
    .map(pms => ({
      pms,
      label: typeof labelForPms === 'function' ? labelForPms(pms) : pms,
      count: counts[pms]
    }))
    .sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count
      }
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    })
}

/**
 * @param {object} hotel
 * @param {string} pmsKey
 * @returns {boolean}
 */
export function hotelHasPms(hotel, pmsKey) {
  if (!pmsKey || pmsKey === 'all') {
    return true
  }
  return readHotelPmsKey(hotel) === String(pmsKey).toLowerCase()
}
